import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { hospitalAPI } from '../../../utils/api';
import DoctorWallet from '../../doctor/wallet/Wallet';

const HospitalWallet = () => {
  const { showError } = useNotification();
  const [hospitalBalance, setHospitalBalance] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHospitalData = async () => {
      try {
        const [balanceRes, doctorsRes] = await Promise.all([
          hospitalAPI.getWallet(),
          hospitalAPI.getDoctors()
        ]);

        if (balanceRes.data?.success) {
          setHospitalBalance(balanceRes.data.data?.balance || 0);
        }

        if (doctorsRes.data?.success) {
          setDoctors(doctorsRes.data.data || []);
        }

      } catch (error) {
        console.error(error);
        showError('Failed to fetch wallet details');
      } finally {
        setLoading(false);
      }
    };

    fetchHospitalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="text-center py-8">Loading wallet...</div>;

  // Calculate total doctor earnings (just for display, actual balance is what is in wallet)
  const totalDoctorEarnings = doctors.reduce((sum, doc) => sum + (doc.wallet_balance || 0), 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">Hospital Wallet</h1>

      {/* Total Balance Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-gold to-yellow-600 rounded-xl p-6 text-dark">
          <div className="text-center">
            <p className="text-lg opacity-80 mb-2">Hospital Balance</p>
            <p className="text-4xl font-bold">₹{hospitalBalance.toFixed(2)}</p>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 flex flex-col justify-center items-center">
          <p className="text-gray-400 mb-2">Total Doctor Earnings</p>
          <p className="text-3xl font-bold text-light">₹{totalDoctorEarnings.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">Aggregated from {doctors.length} doctors</p>
        </div>
      </div>

      {/* Doctor Breakdown */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-gold">Doctor Earnings Breakdown</h3>
        {doctors.length === 0 ? (
          <p className="text-gray-400">No doctors added yet.</p>
        ) : (
          <div className="space-y-3">
            {doctors.map(doc => (
              <div key={doc._id} className="flex justify-between items-center bg-gray-700/50 p-4 rounded-lg">
                <div>
                  <p className="font-medium text-light">{doc.name}</p>
                  <p className="text-xs text-gray-400">{doc.specialization}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gold">₹{(doc.wallet_balance || 0).toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Wallet Balance</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reuse Withdrawal Logic (but for Hospital) */}
      {/* We can temporarily wrap DoctorWallet but pass hospital context if we wanted, 
                but DoctorWallet reads user from context. 
                Since Hospital IS the user in context, DoctorWallet handles the withdrawal UI correctly for the Hospital's OWN balance.
            */}
      <div className="border-t border-gray-700 pt-6">
        <h2 className="text-xl font-bold text-white mb-4">Request Withdrawal</h2>
        <DoctorWallet />
      </div>
    </div>
  );
};

export default HospitalWallet;
